import { css } from '@emotion/react';

import { useTranslation } from 'next-i18next';
import { useRouter } from 'next/router';

import { Spacing } from 'chatie-design-system';

import { media } from '@styles/css';

import ProfileInfoGalleryCard from '@components/characters/ProfileInfoGalleryCard';
import SwipeableListContainer from '@components/common/SwipableListContainer';

import { IProfileByInfoCardFormGallery } from '@base/outlookModel';
import { Platform } from '@constants';
import { useGlobalContext } from '@contexts/global';
import { toQueryString } from '@utils/misc';
import { useWebView } from '@utils/webview';

import OutlookButton from './OutlookButton';

const containerCSS = () => css`
  .header {
    display: flex;
    justify-content: space-between;
    padding: 10px ${Spacing.medium}px 0;
    ${media.tablet} {
      padding: 10px ${Spacing.xlarge}px 0;
    }
    ${media.desktop} {
      padding: 10px ${Spacing.xxlarge}px 0;
    }
  }

  .cardListContainer {
    display: flex;
    margin-top: ${Spacing.large}px;
    gap: 10px;
    padding: 0 ${Spacing.medium}px;
    padding-bottom: 10px;
    ${media.tablet} {
      padding: 0 ${Spacing.xlarge}px;
      padding-bottom: 10px;
    }
    ${media.desktop} {
      padding: 0 ${Spacing.xxlarge}px;
      padding-bottom: 10px;
    }
  }
`;

export interface ProfileByInfoCardFormGalleryProps {
  data: IProfileByInfoCardFormGallery;
  componentKey: string;
  onClickCard?: (id: number) => void;
  onClickProfile?: (id: number) => void;
}

const ProfileByInfoCardFormGallery = ({
  data,
  componentKey,
  onClickCard,
  onClickProfile,
}: ProfileByInfoCardFormGalleryProps) => {
  const { t } = useTranslation();
  const { title, profiles, button } = data;
  const { web } = useWebView();
  const { platform, isCheming } = useGlobalContext();
  const router = useRouter();

  const handleClickOutlookButton = () => {
    if (button.url && button.url !== '') {
      if (platform === Platform.web) {
        router.push(button.url);
      }
      web.openPage(button.url);
    } else {
      const url = `/play/characters/${toQueryString({
        componentKey,
      })}`;
      if (platform === Platform.web) {
        router.push(url);
      }
      web.openPage(url);
    }
  };

  return (
    <div css={containerCSS}>
      <div className="header">
        <h5 suppressHydrationWarning>{t(title)}</h5>
        {button && (
          <OutlookButton
            type={button.type as 'arrow'}
            text={button.text}
            onClickButton={handleClickOutlookButton}
          />
        )}
      </div>

      <SwipeableListContainer className="galleryCardListContainer">
        <div className="cardListContainer">
          {profiles.map((item) => (
            <ProfileInfoGalleryCard
              key={`profile_${item.id}`}
              data={item}
              size={'w128'}
              hanelOnClickCard={
                isCheming || item?.payload?.dynamic_object?.type === 'official'
                  ? onClickProfile
                  : onClickCard
              }
            />
          ))}
        </div>
      </SwipeableListContainer>
    </div>
  );
};

export default ProfileByInfoCardFormGallery;
